<script setup lang="ts">
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useTransactionStore } from '@/store/use-transaction-store';
import { useRouter } from 'vue-router';
import { Button } from '@/components/ui/button';
import { 
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle 
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  FileText,
  Search,
  ArrowRight,
  BarChart3,
  FileSpreadsheet,
  Calendar,
  RefreshCw,
  Sunset,
  Merge,
  UserCheck,
} from 'lucide-vue-next';

const router = useRouter();

// Get store
const transactionStore = useTransactionStore()

interface Report {
  name: string;
  title: string;
  route: string;
  description: string;
  buttonText: string;
  reportType?: string
}

const reports = ref<Report[]>([
  {
    name: 'Rollover Report',
    title: 'Rollover Report',
    route: '/transactions',
    reportType: 'rollover',
    description: 'Rollover Report provides operational transparency into treatment plans extended into new benefit periods. It highlights plans that remain active, suspended, or expired, including carried-over balances, remaining allowed quantities, and updated eligibility.',
    buttonText: 'Rollover Report'
  },
  {
    name: 'Sync Practice Report',
    title: 'Sync Practices',
    route: '/transactions',
    reportType: 'syncing',
    description: 'The Sync Practice Types Report provides visibility into how practice type configurations are synchronized across systems. It highlights mismatches, missing mappings, and successful alignments between source and downstream platforms.',
    buttonText: 'Sync Practices Report'
  },
  {
    name: 'Tariff Codes Upload Report',
    title: 'Tariff Codes Upload Report',
    route: '/transactions',
    reportType: 'add_tariff_codes',
    description: 'This report facilitates the bulk upload of new or updated tariff codes to a Treatment Plan.',
    buttonText: 'Tariff Codes Upload Report'
  },
  {
    name: 'Tariff Codes Merge Report',
    title: 'Tariff Codes Merge Report',
    route: '/transactions',
    reportType: 'merge_tariff_codes',
    description: 'The Tariff Codes Merge Report provides visibility into the merging process of duplicate or similar tariff codes into a single unified code',
    buttonText: 'Tariff Codes Merge Report'
  },
  {
    name: 'Tariff Code Allowed Quantity Report',
    title: 'Tariff Code Allowed Quantity Report',
    route: '/transactions',
    reportType: 'update_allowed_qty',
    description: 'The Tariff Code Allowed Quantity Report provides visibility into the update of allowed quantities process for each tariff code, including their current usage, remaining balances, and impact across treatment plans, billing, and operational systems.',
    buttonText: 'Tariff Code Allowed Quantity Report'
  },
  {
    name: 'Tariff Code Sunsetting Report',
    title: 'Tariff Code Sunsetting Report',
    route: '/transactions',
    reportType: 'sunset',
    description: 'The Tariff Codes Sunsetting Report provides visibility into tariff codes scheduled for retirement, including their current usage, dependencies, and impact across treatment plans, billing, and operational systems.',
    buttonText: 'Tariff Code Sunsetting Report'
  },
  {
    name: 'Membership Status Report',
    title: 'Membership Status Report',
    route: '/transactions',
    reportType: 'membership_status_check',
    description: 'The Membership Status Report provides comprehensive insights into the status of individual memberships, including their active, suspended, or expired status, and impact on treatment plans, billing, and operational systems.',
    buttonText: 'Membership Status Report'
  }
]);

const searchQuery = ref('');

const filteredReports = ref(reports.value.filter(report =>
  !searchQuery.value ||
  report.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
  report.description.toLowerCase().includes(searchQuery.value.toLowerCase())
));

const getReportIcon = (reportName: string) => {
  switch (reportName) {
    case 'Rollover Report':
      return Calendar;
    case 'Sync Practice Report':
      return RefreshCw;
    case 'Tariff Code Sunsetting Report':
      return Sunset;
    case 'Tariff Codes Upload Report':
      return FileSpreadsheet;
     case 'Tariff Codes Merge Report':
      return Merge;
    case 'Tariff Code Allowed Quantity Report':
      return BarChart3;
    case 'Membership Status Report':
      return UserCheck; 
    default:
      return FileText;
  }
};

const getReportColor = (reportName: string) => {
  switch (reportName) {
    case 'Rollover Report':
      return 'bg-blue-100 text-blue-600';
    case 'Sync Practice Report':
      return 'bg-green-100 text-green-600';
    case 'Tariff Code Sunsetting Report':
      return 'bg-orange-100 text-orange-600';
    case 'Tariff Codes Merge Report':
      return 'bg-purple-100 text-purple-600';
    case 'Membership Status Report':
      return 'bg-yellow-100 text-yellow-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const setTranasctionTypeNavigateToReport = (route: string, transactionType: string) => {
  // Here we goig to set Transaction type
  transactionStore.setTransactionType(transactionType)
  router.push(route);
};

const handleSearch = () => {
  filteredReports.value = reports.value.filter(report =>
    !searchQuery.value ||
    report.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    report.description.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
};
</script>

<template>
  <div class="min-h-screen bg-gray-50 p-6">
    <div class="mx-auto space-y-6">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">Reports Dashboard</h1>
          <p class="text-gray-600 mt-1">Access and generate operational reports</p>
        </div>
        <div class="flex items-center gap-3">
          <Badge variant="outline" class="px-3 py-1">
            {{ filteredReports.length }} Reports Available
          </Badge>
        </div>
      </div>

      <!-- Search Bar -->
      <Card>
        <CardContent class="pt-6">
          <div class="relative">
            <Search class="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              v-model="searchQuery"
              @input="handleSearch"
              placeholder="Search reports by name or description..."
              class="pl-10 h-12 text-base"
            />
          </div>
        </CardContent>
      </Card>

      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent class="pt-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600">Total Reports</p>
                <p class="text-3xl font-bold text-gray-900 mt-2">{{ reports.length }}</p>
              </div>
              <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileSpreadsheet class="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent class="pt-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600">Operational Reports</p>
                <p class="text-3xl font-bold text-green-600 mt-2">3</p>
              </div>
              <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <BarChart3 class="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent class="pt-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600">Available Now</p>
                <p class="text-3xl font-bold text-purple-600 mt-2">{{ filteredReports.length }}</p>
              </div>
              <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <FileText class="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <!-- Reports Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card
          v-for="report in filteredReports"
          :key="report.name"
          class="hover:shadow-lg transition-shadow cursor-pointer group"
          @click="setTranasctionTypeNavigateToReport(report.route, report.reportType)"
        >
          <CardHeader>
            <div class="flex items-start justify-between mb-4">
              <div :class="['w-14 h-14 rounded-lg flex items-center justify-center', getReportColor(report.name)]">
                <component :is="getReportIcon(report.name)" class="h-7 w-7" />
              </div>
              <Badge variant="secondary" class="text-xs bg-gray-100 rounded-lg hover:bg-green-500 hover:text-white">
                Report
              </Badge>
            </div>
            <CardTitle class="text-xl group-hover:text-blue-600 transition-colors">
              {{ report.title }}
            </CardTitle>
            <CardDescription class="mt-2 text-sm leading-relaxed min-h-[60px]">
              {{ report.description }}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              class="w-full text-white bg-gradient-to-r from-sky-800  to-[#be9b2d] group-hover:text-white group-hover:border-sky-700 transition-all"
              @click.stop="setTranasctionTypeNavigateToReport(report.route, report.reportType)"
            >
              {{ report.buttonText }}
              <ArrowRight class="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>

      <!-- Empty State -->
      <Card v-if="filteredReports.length === 0" class="border-dashed">
        <CardContent class="flex flex-col items-center justify-center py-12">
          <FileText class="h-16 w-16 text-gray-400 mb-4" />
          <p class="text-gray-600 text-lg font-medium">No reports found</p>
          <p class="text-gray-500 text-sm mt-1">Try adjusting your search query</p>
          <Button
            variant="outline"
            class="mt-4"
            @click="searchQuery = ''; handleSearch()"
          >
            Clear Search
          </Button>
        </CardContent>
      </Card>
    </div>
  </div>
</template>